import type {ReactNode} from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {XIcon} from '@primer/octicons-react'
import {Heading, IconButton, useFocusTrap, useOnEscapePress} from '@primer/react'
import {useSlots} from '@primer/react/experimental'
import {clsx} from 'clsx'
import styles from './Panel.module.css'
import {PanelNavigationDropdown} from './PanelNavigationDropdown'
import {
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
} from '../../contexts/DockedPanelContext'
import type {DockedPanel} from '../../contexts/DockedPanelContext'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useViewportFitHeight} from '../../hooks/use-viewport-fit-height'
import {useDialogFillHeight} from '../../hooks/use-dialog-fill-height'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export const panelHeadings: Record<DockedPanel, string> = {
  agentSessions: 'Agent sessions',
  comments: 'Comments',
  alerts: 'Alerts',
  overview: 'Overview',
  mergeStatus: 'Merge status',
}

interface PanelHeaderProps {
  children: ReactNode
}

function PanelHeader({children}: PanelHeaderProps) {
  return <>{children}</>
}

interface PanelBodyProps {
  children: ReactNode
  className?: string
}

function PanelBody({children, className}: PanelBodyProps) {
  return <div className={clsx('flex-1 overflow-auto', className ?? 'tmp-p-3')}>{children}</div>
}

interface PanelFooterProps {
  children: ReactNode
}

function PanelFooter({children}: PanelFooterProps) {
  return <>{children}</>
}

interface PanelViewportFillProps {
  children: ReactNode
}

/**
 * Opt-in wrapper that constrains its children to the visible viewport area.
 *
 * Use inside `Panel.Body` when the panel content has its own internal scroll
 * management (e.g. a chat with a sticky input at the bottom) and must not
 * extend below the viewport -- even before the sticky sidebar fully takes effect.
 */
function PanelViewportFill({children}: PanelViewportFillProps) {
  const ref = useRef<HTMLDivElement>(null)
  useViewportFitHeight(ref)
  return (
    <div ref={ref} className={styles.ViewportFill}>
      {children}
    </div>
  )
}

interface PanelDialogFillProps {
  children: ReactNode
}

/**
 * Wrapper for Dialog body content that fills the available height.
 *
 * Primer's Dialog body gets its height from flex layout, which breaks
 * CSS percentage-height resolution for children.  This component
 * uses a ResizeObserver on the parent to set an explicit pixel height.
 */
function PanelDialogFill({children}: PanelDialogFillProps) {
  const ref = useRef<HTMLDivElement>(null)
  useDialogFillHeight(ref)
  return (
    <div ref={ref} className={styles.DialogFill}>
      {children}
    </div>
  )
}

interface PanelProps {
  panelName: DockedPanel
  children: ReactNode
}

/**
 * Self-contained compound component for docked side panel content.
 *
 * Renders the panel heading (derived from `panelName`), a close button with
 * managed focus refs, and `Panel.Header`, `Panel.Body`, and `Panel.Footer`
 * slot sub-components laid out in a flex column. The body region scrolls
 * while header and footer remain fixed.
 *
 * Can be rendered directly inside `DockedSidePanelContent` or portaled
 * into the docked panel's content slot via `createPortal`.
 */
function Panel({panelName, children}: PanelProps) {
  const {closeButtonRef, returnFocusRef} = useDockedPanelFocusRefs()
  const {closePanel} = useDockedPanelActions()
  const isCompact = useIsDockedPanelCompact()
  const containerRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(() => window.matchMedia('(max-width: calc(48rem - 0.02px))').matches)

  const handleClose = useCallback(() => {
    const returnTarget = returnFocusRef.current
    closePanel()
    // Return focus to the element that opened the panel
    if (returnTarget) {
      requestAnimationFrame(() => returnTarget.focus())
    }
  }, [closePanel, returnFocusRef])

  const isPanelSelectorEnabled = isFeatureEnabled('prs_docked_side_panel_selector')
  const heading = panelHeadings[panelName]

  const [slots] = useSlots(children, {
    header: PanelHeader,
    body: PanelBody,
    footer: PanelFooter,
  })

  useFocusTrap({containerRef, disabled: !isFullscreen})

  // Only close on Escape if focus is within the panel
  useOnEscapePress(() => {
    if (containerRef.current?.contains(document.activeElement)) {
      handleClose()
    }
  }, [handleClose])

  // When SplitPageLayout.Sidebar transitions to fullscreen,
  // move focus to the close button so focus isn't behind the panel.
  useEffect(() => {
    const mediaQuery = ssrSafeWindow?.matchMedia('(max-width: calc(48rem - 0.02px))')

    const handleChange = (event: MediaQueryListEvent) => {
      setIsFullscreen(event.matches)
      if (event.matches && !containerRef.current?.contains(document.activeElement)) {
        closeButtonRef.current?.focus()
      }
    }

    mediaQuery?.addEventListener('change', handleChange)
    return () => mediaQuery?.removeEventListener('change', handleChange)
  }, [closeButtonRef])

  return (
    <div ref={containerRef} className="d-flex flex-column height-full">
      {/* In compact mode the Dialog provides the heading and close button */}
      {(!isCompact || slots.header) && (
        <div className={styles.Header}>
          {!isCompact && (
            <div className={styles.HeaderContent}>
              <Heading as="h3" className={styles.Heading}>
                {isPanelSelectorEnabled ? <PanelNavigationDropdown /> : <>{heading}</>}
              </Heading>
              <IconButton
                ref={closeButtonRef}
                aria-label="Close side panel"
                icon={XIcon}
                variant="invisible"
                size="medium"
                onClick={handleClose}
              />
            </div>
          )}
          {slots.header}
        </div>
      )}
      {slots.body}
      {slots.footer && <div className="tmp-p-3">{slots.footer}</div>}
    </div>
  )
}

Panel.Header = PanelHeader
Panel.Body = PanelBody
Panel.Footer = PanelFooter
Panel.ViewportFill = PanelViewportFill
Panel.DialogFill = PanelDialogFill

export {Panel}
